<template>
  <v-container fluid>
    <v-overlay :value="pageLoading">
      <v-progress-circular
        indeterminate
        size="64"
        color="color_green"
      />
    </v-overlay>

    <v-alert
      v-if="!pageLoading && showEmptyYearHint"
      type="info"
      dense
      class="mb-4"
    >
      No call data for {{ selectedYear }}. Try another year from the selector, or run the DB migration if tables are missing.
    </v-alert>

    <!-- ── Global toolbar ── -->
    <v-row
      align="center"
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="auto"
      >
        <div class="text-h5 font-weight-bold">
          Statistics
        </div>
        <div class="text-caption grey--text">
          API call analytics per year
        </div>
      </v-col>
      <v-spacer />

      <v-col
        cols="12"
        sm="3"
        md="2"
      >
        <v-select
          v-model="selectedYear"
          :items="availableYears"
          label="Year"
          dense
          outlined
          hide-details
          prepend-inner-icon="mdi-calendar"
          @change="onYearChange"
        />
      </v-col>

      <v-col
        cols="6"
        sm="2"
      >
        <v-text-field
          v-model="dateFrom"
          label="From"
          type="date"
          dense
          outlined
          hide-details
          clearable
          :min="yearDateMin"
          :max="dateFromMax"
        />
      </v-col>

      <v-col
        cols="6"
        sm="2"
      >
        <v-text-field
          v-model="dateTo"
          label="To"
          type="date"
          dense
          outlined
          clearable
          :min="dateFrom || yearDateMin"
          :max="dateToMax"
          :error-messages="dateRangeError"
          :hide-details="!dateRangeError"
        />
      </v-col>

      <v-col cols="auto">
        <v-btn
          color="color_green"
          dark
          depressed
          :loading="anyLoading"
          @click="applyFilters"
        >
          <v-icon left>
            mdi-filter
          </v-icon>
          Apply
        </v-btn>
      </v-col>

      <v-col cols="auto">
        <v-btn
          text
          depressed
          :disabled="anyLoading"
          @click="loadAll"
        >
          <v-icon left>
            mdi-refresh
          </v-icon>
          Refresh
        </v-btn>
      </v-col>
    </v-row>

    <!-- ── KPI cards ── -->
    <section-error
      v-if="errors.overview && !loadingOverview"
      message="Failed to load overview"
      :loading="loadingOverview"
      @retry="storeCallsOverview"
    />

    <overview-cards
      :overview="overview"
      :year="selectedYear"
      :loading="loadingOverview"
      :date-from="storeDateFrom"
      :date-to="storeDateTo"
    />

    <global-breakdown-cards :overview="overview" />

    <!-- ── Main tabs ── -->
    <v-tabs
      v-model="tab"
      background-color="color_green"
      slider-color="#00802d"
      slider-size="4"
      dark
      class="mt-4 rounded-lg"
      show-arrows
    >
      <v-tab>Top Callers</v-tab>
      <v-tab>By Service</v-tab>

      <v-tabs-items
        v-model="tab"
        class="mt-3"
      >
        <!-- ── TAB 0: Top Callers ── -->
        <v-tab-item>
          <section-error
            v-if="errors.topCallers && !loadingTopCallers"
            message="Failed to load top callers"
            @retry="storeTopCallers(100)"
          />

          <section-error
            v-if="selectedUser && errors.dailyStats && !loadingDailyStats"
            message="Failed to load daily trend"
            @retry="storeDailyStats(selectedUser.username)"
          />

          <top-callers-table
            :items="topCallers"
            :loading="loadingTopCallers"
            @drill-down="openUserDetail"
          />

          <caller-breakdown-panel
            v-if="selectedUser"
            :breakdown="callerBreakdown"
            :username="selectedUser.username"
            :loading="loadingCallerBreakdown"
            :daily-stats="dailyStats"
            :loading-daily-stats="loadingDailyStats"
            :year="selectedYear"
            @close="closeUserDetail"
          />

          <div
            v-if="selectedUser && showCallsDetail"
            class="mt-4"
          >
            <calls-by-user-table
              :items="callsByUser"
              :username="selectedUser.username"
              :year="selectedYear"
              :loading="loadingCallsByUser"
              :initial-date-from="dateFrom"
              :initial-date-to="dateTo"
              @search="onUserSearch"
              @close="showCallsDetail = false"
            />
          </div>

          <v-row
            v-if="selectedUser"
            class="mt-2"
            dense
          >
            <v-col cols="auto">
              <v-btn
                small
                text
                color="primary"
                @click="showCallsDetail = !showCallsDetail"
              >
                <v-icon
                  left
                  small
                >
                  mdi-format-list-bulleted
                </v-icon>
                {{ showCallsDetail ? 'Hide' : 'Show' }} raw call log
              </v-btn>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- ── TAB 1: By Service ── -->
        <v-tab-item>
          <section-error
            v-if="errors.serviceStats && !loadingServiceStats"
            message="Failed to load service stats"
            @retry="storeServiceStats"
          />

          <service-bar-chart
            :items="serviceStats"
            :loading="loadingServiceStats"
          />

          <v-card
            elevation="2"
            rounded="lg"
            class="mt-4"
          >
            <v-toolbar
              flat
              dense
              color="color_green"
              dark
            >
              <v-toolbar-title class="text-subtitle-2">
                Service Breakdown Table
              </v-toolbar-title>
            </v-toolbar>
            <v-data-table
              :headers="serviceHeaders"
              :items="serviceStats"
              :loading="loadingServiceStats"
              dense
              hide-default-footer
              :items-per-page="-1"
              show-expand
              single-expand
            >
              <template #item.call_count="{ item }">
                <span class="font-weight-bold">{{ item.call_count.toLocaleString('it-IT') }}</span>
              </template>
              <template #item.workspace_count="{ item }">
                {{ item.workspace_count.toLocaleString('it-IT') }}
              </template>
              <template #item.layer_count="{ item }">
                {{ item.layer_count.toLocaleString('it-IT') }}
              </template>
              <template #item.time_duration_count="{ item }">
                {{ item.time_duration_count.toLocaleString('it-IT') }}
              </template>
              <template #item.timezone_count="{ item }">
                {{ item.timezone_count.toLocaleString('it-IT') }}
              </template>
              <template #item.output_fmt_count="{ item }">
                {{ item.output_fmt_count.toLocaleString('it-IT') }}
              </template>
              <template #item.pct="{ item }">
                <v-progress-linear
                  :value="pct(item.call_count)"
                  color="color_green"
                  height="10"
                  rounded
                  background-color="grey lighten-3"
                />
              </template>
              <template #expanded-item="{ headers, item }">
                <td
                  :colspan="headers.length"
                  class="pa-4 grey lighten-5"
                >
                  <v-row dense>
                    <v-col
                      v-for="section in serviceBreakdownSections"
                      :key="section.key"
                      cols="12"
                      sm="6"
                      lg="3"
                    >
                      <div class="text-subtitle-2 mb-2">
                        {{ section.title }}
                      </div>
                      <service-breakdown-list
                        :items="item[section.key]"
                        :empty-label="section.emptyLabel"
                      />
                    </v-col>
                  </v-row>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  yearDateBounds,
  currentMonthRange,
  lastDayOfMonth,
  sameMonth
} from '@/api/statisticsParams.js';

export default {
  name: 'Statistics',

  components: {
    SectionError: () => import('@/components/statistics/SectionError'),
    OverviewCards: () => import('@/components/statistics/OverviewCards'),
    GlobalBreakdownCards: () => import('@/components/statistics/GlobalBreakdownCards'),
    TopCallersTable: () => import('@/components/statistics/TopCallersTable'),
    CallerBreakdownPanel: () => import('@/components/statistics/CallerBreakdownPanel'),
    CallsByUserTable: () => import('@/components/statistics/CallsByUserTable'),
    ServiceBarChart: () => import('@/components/statistics/ServiceBarChart'),
    ServiceBreakdownList: () => import('@/components/statistics/ServiceBreakdownList')
  },

  data() {
    return {
      pageLoading: false,
      tab: 0,
      selectedUser: null,
      showCallsDetail: false,
      dateFrom: null,
      dateTo: null,
      serviceHeaders: [
        { text: '', value: 'data-table-expand' },
        { text: 'Service', value: 'service' },
        { text: 'Calls', value: 'call_count', align: 'end' },
        { text: 'WS', value: 'workspace_count', align: 'end' },
        { text: 'Layers', value: 'layer_count', align: 'end' },
        { text: 'Span', value: 'time_duration_count', align: 'end' },
        { text: 'TZ', value: 'timezone_count', align: 'end' },
        { text: 'Fmt', value: 'output_fmt_count', align: 'end' },
        { text: '% Share', value: 'pct', sortable: false, width: 160 }
      ],
      serviceBreakdownSections: [
        { key: 'workspaces', title: 'Workspaces', emptyLabel: 'No workspace data' },
        { key: 'layers', title: 'Layers (top)', emptyLabel: 'No layer data' },
        { key: 'time_durations', title: 'Request span', emptyLabel: 'No time range data' },
        { key: 'timezones', title: 'Timezones', emptyLabel: 'No timezone data' },
        { key: 'output_formats', title: 'Output formats', emptyLabel: 'No output format data' }
      ]
    };
  },

  computed: {
    ...mapState('statistics', [
      'availableYears',
      'overview',
      'topCallers',
      'callerBreakdown',
      'callsByUser',
      'serviceStats',
      'dailyStats',
      'errors',
      'serviceStatsLoaded',
      'loadingOverview',
      'loadingTopCallers',
      'loadingCallerBreakdown',
      'loadingCallsByUser',
      'loadingServiceStats',
      'loadingDailyStats'
    ]),
    storeDateFrom() {
      return this.$store.state.statistics.dateFrom;
    },
    storeDateTo() {
      return this.$store.state.statistics.dateTo;
    },
    selectedYear: {
      get() {
        return this.$store.state.statistics.selectedYear;
      },
      set(val) {
        this.setSelectedYear(val);
      }
    },
    anyLoading() {
      return (
        this.loadingOverview ||
        this.loadingTopCallers ||
        this.loadingServiceStats ||
        this.loadingDailyStats
      );
    },
    serviceTotalCalls() {
      return this.serviceStats.reduce((s, i) => s + i.call_count, 0) || 1;
    },
    showEmptyYearHint() {
      return (
        this.overview &&
        !Object.values(this.errors).some(Boolean) &&
        this.overview.total_calls === 0 &&
        !this.topCallers.length
      );
    },
    yearDateMin() {
      return yearDateBounds(this.selectedYear).min;
    },
    yearDateMax() {
      return yearDateBounds(this.selectedYear).max;
    },
    dateFromMax() {
      return this.dateTo || this.yearDateMax;
    },
    dateToMax() {
      if (!this.dateFrom) {
        return this.yearDateMax;
      }

      const monthEnd = lastDayOfMonth(this.dateFrom);

      return monthEnd < this.yearDateMax ? monthEnd : this.yearDateMax;
    },
    dateRangeError() {
      if (this.dateFrom && this.dateTo && !sameMonth(this.dateFrom, this.dateTo)) {
        return 'Max range: same month (30 days)';
      }

      return null;
    }
  },

  watch: {
    tab(val) {
      if (val === 1) {
        this.ensureServiceStats();
      }
    },
    dateFrom(newVal) {
      if (newVal && this.dateTo && !sameMonth(newVal, this.dateTo)) {
        this.dateTo = null;
      }
    }
  },

  async mounted() {
    this.pageLoading = true;
    const { date_from, date_to } = currentMonthRange();

    this.dateFrom = date_from;
    this.dateTo = date_to;
    this.setDateRange({ date_from, date_to });

    try {
      await this.initStatisticsPage();
    } finally {
      this.pageLoading = false;
    }
  },

  methods: {
    ...mapActions('statistics', [
      'initStatisticsPage',
      'loadStatisticsData',
      'setSelectedYear',
      'setDateRange',
      'storeCallsByUser',
      'storeCallerBreakdown',
      'storeCallsOverview',
      'storeTopCallers',
      'storeServiceStats',
      'storeDailyStats'
    ]),

    async ensureServiceStats() {
      if (
        this.tab === 1 &&
        !this.serviceStatsLoaded &&
        !this.errors.serviceStats &&
        !this.loadingServiceStats
      ) {
        await this.storeServiceStats();
      }
    },

    async applyFilters() {
      if (this.dateRangeError) {
        return;
      }

      this.setDateRange({
        date_from: this.dateFrom || null,
        date_to: this.dateTo || null
      });
      this.dateFrom = this.$store.state.statistics.dateFrom;
      this.dateTo = this.$store.state.statistics.dateTo;
      await this.loadAll();
    },

    async loadAll() {
      this.selectedUser = null;
      this.showCallsDetail = false;
      this.pageLoading = true;

      try {
        await this.loadStatisticsData();
        await this.ensureServiceStats();
      } finally {
        this.pageLoading = false;
      }
    },

    async onYearChange(year) {
      this.setSelectedYear(year);
      const currentYear = new Date().getFullYear();

      if (year === currentYear) {
        const { date_from, date_to } = currentMonthRange();

        this.dateFrom = date_from;
        this.dateTo = date_to;
        this.setDateRange({ date_from, date_to });
      } else {
        this.dateFrom = null;
        this.dateTo = null;
      }

      this.selectedUser = null;
      await this.loadAll();
    },

    async openUserDetail(user) {
      this.selectedUser = user;
      this.showCallsDetail = false;
      await Promise.all([
        this.storeCallerBreakdown(user.username),
        this.storeDailyStats(user.username)
      ]);
    },

    closeUserDetail() {
      this.selectedUser = null;
      this.showCallsDetail = false;
    },

    async onUserSearch({ date_from, date_to }) {
      await this.storeCallsByUser({
        username: this.selectedUser.username,
        date_from,
        date_to
      });
    },

    pct(callCount) {
      return Math.round((callCount / this.serviceTotalCalls) * 100);
    }
  }
};
</script>

<style scoped>
.v-tabs-items {
  background: transparent !important;
}
</style>
